import { Fragment, useRef } from "react";
import {
    motion,
    useAnimationFrame,
    useMotionValue,
    useScroll,
    useSpring,
    useTransform,
    useVelocity,
    wrap,
} from "framer-motion";
import { useTranslation } from "react-i18next";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import "../../styles/interactive.css";

const BASE_SPEED = 2.2;
const COPIES = 4;

/**
 * Oversized text band that drifts sideways, speeds up with scroll velocity and
 * reverses when the reader scrolls back up.
 */
export default function Marquee() {
    const { t } = useTranslation();
    const reducedMotion = usePrefersReducedMotion();
    const words = t("hero.focus").split("/").map((word) => word.trim()).filter(Boolean);

    const baseX = useMotionValue(0);
    const { scrollY } = useScroll();
    const velocity = useSpring(useVelocity(scrollY), { damping: 50, stiffness: 400 });
    const velocityFactor = useTransform(velocity, [0, 1000], [0, 4], { clamp: false });
    const direction = useRef(1);
    // One copy is 1/COPIES of the track, so wrapping over that range loops seamlessly.
    const x = useTransform(baseX, (value) => `${wrap(-100 / COPIES, 0, value)}%`);

    useAnimationFrame((_, delta) => {
        if (reducedMotion) return;
        const factor = velocityFactor.get();
        if (factor < 0) direction.current = -1;
        else if (factor > 0) direction.current = 1;
        const move = direction.current * BASE_SPEED * (delta / 1000) * (1 + Math.abs(factor));
        baseX.set(baseX.get() - move);
    });

    return (
        <div className="marquee" aria-hidden="true">
            <motion.div className="marquee-track" style={{ x }}>
                {Array.from({ length: COPIES }, (_, copy) => (
                    <div className="marquee-copy" key={copy}>
                        {words.map((word, index) => (
                            <Fragment key={`${copy}-${index}`}>
                                <span className={index % 2 === 1 ? "marquee-word marquee-word-outline" : "marquee-word"}>{word}</span>
                                <span className="marquee-sep" />
                            </Fragment>
                        ))}
                    </div>
                ))}
            </motion.div>
        </div>
    );
}
